import { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  TablePagination,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  History as HistoryIconIcon,
  CheckCircle,
  Error,
  Phone as PhoneIcon,
  CreditCard as LicenseIcon,
  AccessTime as TimeIcon,
  Close as CloseIcon,
  LocalShipping as TruckIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "../utils/api-config";
import type { AxiosResponse } from "axios";
import { useSnackbar } from "notistack";
import { Link } from "react-router-dom";
import { QrCode as QrCodeIcon } from "@mui/icons-material";
import DriverQRCode from "../components/DriverQRCode";
import { useForm, Controller } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// Types et interfaces
interface HeavyVehicleInfo {
  driverCardExpiryDate?: string;
  fcoExpiryDate?: string;
  kacesExpiryDate?: string;
  adrExpiryDate?: string;
}

interface Chauffeur {
  _id?: string;
  driverId: string;
  firstName: string;
  lastName: string;
  address?: string;
  postalCode?: string;
  city?: string;
  department?: string;
  country?: string;
  phone1: string;
  phone2?: string;
  email?: string;
  licenseType: "AM" | "A1" | "A2" | "A" | "B1" | "B" | "C1" | "C" | "D1" | "D" | "BE" | "C1E" | "CE" | "D1E" | "DE";
  licenseTypes?: string; // Tous les types: "AM, A1, B, B1"
  licenseNumber: string;
  licenseExpiryDate?: string;
  licenseIssueDate?: string;
  licenseIssuedBy?: string;
  heavyVehicleInfo?: HeavyVehicleInfo;
  dataModifications?: Array<{
    date: string;
    modifiedBy: string;
    source: string;
    changes: Array<{ field: string; oldValue: any; newValue: any }>;
    comment?: string;
  }>;
  status: "disponible" | "indisponible" | "inactif";
}

// Constantes
const licenseTypes = [
  { value: "AM", label: "AM - Cyclomoteurs" },
  { value: "A1", label: "A1 - Motos légères" },
  { value: "A2", label: "A2 - Motos puissance intermédiaire" },
  { value: "A", label: "A - Toutes les motos" },
  { value: "B1", label: "B1 - Quadricycles lourds à moteur" },
  { value: "B", label: "B - Véhicules léger <3T5" },
  { value: "C1", label: "C1 - Poids lourd entre 3T5 et 7T5" },
  { value: "C", label: "C - Poids lourd" },
  { value: "D1", label: "D1 - Transport <16 Voyageurs" },
  { value: "D", label: "D - Autocar" },
  { value: "BE", label: "BE - Catégorie B avec remorque" },
  { value: "C1E", label: "C1E - Catégorie C1 avec remorque" },
  { value: "CE", label: "CE - Poids lourd avec remorque" },
  { value: "D1E", label: "D1E - Catégorie D1 avec remorque" },
  { value: "DE", label: "DE - Autocar avec remorque" },
];

// Labels pour les champs (historique des modifications)
const FIELD_LABELS: Record<string, string> = {
  firstName: 'Prénom',
  lastName: 'Nom',
  phone1: 'Téléphone',
  phone2: 'Téléphone 2',
  email: 'Email',
  address: 'Adresse',
  city: 'Ville',
  country: 'Pays',
  licenseType: 'Type de permis',
  licenseTypes: 'Types de permis',
  licenseNumber: 'N° Permis',
  licenseExpiryDate: 'Fin validité',
  licenseIssueDate: 'Début validité',
  licenseIssuedBy: 'Délivré par',
  status: 'Statut',
  driverId: 'ID Chauffeur',
};

// Schéma de validation
const chauffeurSchema = yup
  .object({
    driverId: yup.string().required("L'ID Chauffeur est requis"),
    firstName: yup.string().required("Le prénom est requis"),
    lastName: yup.string().required("Le nom est requis"),
    phone1: yup.string().required("Le téléphone principal est requis"),
    phone2: yup.string().notRequired(),
    email: yup.string().email("Email invalide").notRequired(),
    address: yup.string().notRequired(),
    postalCode: yup.string().notRequired(),
    city: yup.string().notRequired(),
    department: yup.string().notRequired(),
    country: yup.string().notRequired(),
    licenseType: yup
      .string()
      .oneOf(["AM", "A1", "A2", "A", "B1", "B", "C1", "C", "D1", "D", "BE", "C1E", "CE", "D1E", "DE"], "Type de permis invalide")
      .required("Le type de permis est requis"),
    licenseNumber: yup.string().required("Le numéro de permis est requis"),
    licenseExpiryDate: yup
      .string()
      .required("La date d'expiration est requise"),
    licenseIssueDate: yup.string().notRequired(),
    licenseIssuedBy: yup.string().notRequired(),
    status: yup
      .string()
      .oneOf(["disponible", "indisponible", "inactif"])
      .required(),
  })
  .required();

// Fonctions d'API
const fetchChauffeurs = async (): Promise<Chauffeur[]> => {
  const { data } = await axios.get(`${API_URL}/drivers`);
  return data.drivers || data;
};

// Composant principal
const ChauffeursPage = () => {
  // États
  const [openModal, setOpenModal] = useState(false);
  const [editingChauffeur, setEditingChauffeur] = useState<Chauffeur | null>(
    null
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedChauffeurId, setSelectedChauffeurId] = useState<string | null>(
    null
  );
  const [qrDriver, setQrDriver] = useState<Chauffeur | null>(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [detailsChauffeur, setDetailsChauffeur] = useState<Chauffeur | null>(null);

  // Hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // ✨ SMART DEFAULTS
  const [suggestedDriverId, setSuggestedDriverId] = useState<string>("");

  // Form
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = useForm<Chauffeur>({
    resolver: yupResolver(
      chauffeurSchema
    ) as import("react-hook-form").Resolver<Chauffeur, unknown>,
    defaultValues: {
      driverId: "",
      firstName: "",
      lastName: "",
      phone1: "",
      licenseType: "B",
      licenseNumber: "",
      status: "disponible",
      email: "",
      country: "France", // ✨ Smart default
    },
  });

  const watchedLicenseType = watch("licenseType");
  const watchedPhone1 = watch("phone1");
  const watchedPhone2 = watch("phone2");
  const watchedEmail = watch("email");
  const watchedLicenseIssueDate = watch("licenseIssueDate");

  // Queries et mutations
  const {
    data: chauffeurs = [],
    isLoading,
    isError,
  } = useQuery<Chauffeur[]>({
    queryKey: ["chauffeurs"],
    queryFn: fetchChauffeurs,
  });

  const createOrUpdateMutation = useMutation<AxiosResponse, Error, Chauffeur>({
    mutationFn: (chauffeurData: Chauffeur) => {
      if (editingChauffeur) {
        return axios.put(`${API_URL}/drivers/${editingChauffeur._id}`, chauffeurData);
      }
      return axios.post(`${API_URL}/drivers`, chauffeurData);
    },
    onSuccess: () => {
      enqueueSnackbar(
        `Chauffeur ${editingChauffeur ? "modifié" : "créé"} avec succès`,
        { variant: "success" }
      );
      queryClient.invalidateQueries({ queryKey: ["chauffeurs"] });
      handleCloseModal();
    },
    onError: (error: Error) => {
      enqueueSnackbar(
        `Erreur lors de la ${editingChauffeur ? "modification" : "création"}`,
        { variant: "error" }
      );
      console.error("API Error:", error);
    },
  });

  const deleteMutation = useMutation<AxiosResponse, Error, string>({
    mutationFn: (chauffeurId: string) =>
      axios.delete(`${API_URL}/drivers/${chauffeurId}`),
    onSuccess: () => {
      enqueueSnackbar("Chauffeur supprimé avec succès", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["chauffeurs"] });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          "Erreur lors de la suppression";
      enqueueSnackbar(errorMessage, { variant: "error" });
      console.error("Erreur suppression chauffeur:", error.response?.data || error);
    },
  });

  // ⚡ TOGGLE RAPIDE DE DISPONIBILITÉ
  const toggleStatusMutation = useMutation<AxiosResponse, Error, { id: string; newStatus: string }>({
    mutationFn: ({ id, newStatus }) =>
      axios.patch(`${API_URL}/drivers/${id}/status`, { status: newStatus }),
    onSuccess: (_, { newStatus }) => {
      const statusLabel = newStatus === "disponible" ? "disponible" : newStatus === "indisponible" ? "indisponible" : "inactif";
      enqueueSnackbar(`Statut changé: ${statusLabel}`, { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["chauffeurs"] });
    },
    onError: () => {
      enqueueSnackbar("Erreur lors du changement de statut", { variant: "error" });
    },
  });

  const handleQuickToggleStatus = (chauffeur: Chauffeur, e: React.MouseEvent) => {
    e.stopPropagation();
    const statusOrder: Array<"disponible" | "indisponible" | "inactif"> = ["disponible", "indisponible", "inactif"];
    const currentIndex = statusOrder.indexOf(chauffeur.status);
    const nextStatus = statusOrder[(currentIndex + 1) % statusOrder.length];
    toggleStatusMutation.mutate({ id: chauffeur._id!, newStatus: nextStatus });
  };

  // ✨ AUTO-GÉNÉRATION ID CHAUFFEUR
  const generateNextDriverId = () => {
    if (chauffeurs.length === 0) return "DRV001";
    
    const existingIds = chauffeurs
      .map(c => c.driverId)
      .filter(id => id?.startsWith('DRV'))
      .map(id => parseInt(id.replace('DRV', '') || '0'))
      .filter(num => !isNaN(num));
    
    const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 0;
    return `DRV${String(maxId + 1).padStart(3, '0')}`;
  };

  // ✨ FORMATAGE AUTOMATIQUE TÉLÉPHONE
  const formatPhone = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/);
    if (match) {
      return `${match[1]} ${match[2]} ${match[3]} ${match[4]} ${match[5]}`;
    }
    return cleaned;
  };

  // ✨ CALCUL AUTOMATIQUE DATE EXPIRATION PERMIS
  const calculateExpiryDate = (issueDate: string, licenseType: string): string | null => {
    if (!issueDate) return null;
    
    const validity: Record<string, number> = {
      'B': 15, // 15 ans
      'C': 5,  // 5 ans
      'D': 5,  // 5 ans
      'E': 5   // 5 ans
    };
    
    const years = validity[licenseType] || 15;
    const issue = new Date(issueDate);
    issue.setFullYear(issue.getFullYear() + years);
    return issue.toISOString().split('T')[0];
  };

  // Handlers
  const handleOpenModal = (chauffeur?: Chauffeur) => {
    if (chauffeur) {
      setEditingChauffeur(chauffeur);
      reset(chauffeur);
      setSuggestedDriverId("");
    } else {
      setEditingChauffeur(null);
      const nextId = generateNextDriverId();
      setSuggestedDriverId(nextId);
      
      reset({
        driverId: nextId, // ✨ ID auto-généré
        firstName: "",
        lastName: "",
        phone1: "",
        licenseType: "B",
        licenseNumber: "",
        status: "disponible",
        email: "",
        address: "",
        postalCode: "",
        city: "",
        country: "France", // ✨ Smart default
      });
    }
    setOpenModal(true);
    
    // ✨ Focus automatique sur le premier champ après ouverture
    setTimeout(() => {
      const firstInput = document.querySelector('input[name="firstName"]') as HTMLInputElement;
      firstInput?.focus();
    }, 100);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingChauffeur(null);
    reset();
  };

  const onSubmit: SubmitHandler<Chauffeur> = (data) => {
    createOrUpdateMutation.mutate(data);
  };

  const handleDelete = (chauffeurId: string) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce chauffeur ?")) {
      deleteMutation.mutate(chauffeurId);
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    chauffeurId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedChauffeurId(chauffeurId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedChauffeurId(null);
  };

  const handleChangePage = (event: unknown, newPage: number) =>
    setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtrage et pagination
  const filteredChauffeurs = useMemo(() => {
    if (!searchTerm) return chauffeurs;
    return chauffeurs.filter(
      (ch) =>
        `${ch.firstName} ${ch.lastName}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        ch.driverId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ch.licenseNumber.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [chauffeurs, searchTerm]);

  const paginatedChauffeurs = useMemo(() => {
    if (!Array.isArray(filteredChauffeurs)) return [];
    return filteredChauffeurs.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredChauffeurs, page, rowsPerPage]);

  // Loading et error states
  if (isLoading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );

  if (isError)
    return (
      <Typography color="error" sx={{ textAlign: "center", mt: 4 }}>
        Erreur lors du chargement des chauffeurs.
      </Typography>
    );

  // Rendu
  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        background: "linear-gradient(135deg, #e3f0ff 0%, #f9f9f9 100%)",
        py: { xs: 2, md: 4 },
        px: { xs: 0, md: 0 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1300,
          mx: "auto",
          boxShadow: 3,
          borderRadius: 4,
          background: "#fff",
          p: { xs: 1.5, sm: 3, md: 4 },
          mt: { xs: 1, md: 3 },
          mb: { xs: 2, md: 4 },
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* En-tête */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Button
              component={Link}
              to="/"
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              color="primary"
              sx={{ borderRadius: 2 }}
            >
              Tableau de bord
            </Button>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
            >
              Gestion des Chauffeurs
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "center", sm: "flex-end" },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<span>📥</span>}
              component={Link}
              to="/chauffeurs/import"
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                flex: { xs: 1, sm: "none" },
                maxWidth: { xs: "300px", sm: "none" },
              }}
            >
              Import massif
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                flex: { xs: 1, sm: "none" },
                maxWidth: { xs: "300px", sm: "none" },
              }}
            >
              Ajouter un Chauffeur
            </Button>
          </Box>
        </Box>

        {/* Barre de recherche */}
        <TextField
          variant="outlined"
          size="small"
          placeholder="Rechercher un chauffeur (nom, ID, permis)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2.5,
            width: "100%",
          }}
        />

        {/* Liste : Tableau sur Desktop, Cartes sur Mobile */}
        {!isMobile ? (
          <TableContainer
            sx={{
              flexGrow: 1,
              borderRadius: 2,
              overflow: "auto",
              maxWidth: "100%",
              "& .MuiTableCell-root": {
                whiteSpace: "nowrap",
                px: { xs: 1, sm: 2 },
              },
              "& .MuiTableCell-head": {
                backgroundColor: "grey.100",
                fontWeight: "bold",
              },
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nom Complet</TableCell>
                  <TableCell>Téléphone</TableCell>
                  <TableCell>Type de permis</TableCell>
                  <TableCell>N° Permis</TableCell>
                  <TableCell>Délivré par</TableCell>
                  <TableCell>Début validité</TableCell>
                  <TableCell>Fin validité</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedChauffeurs.length > 0 ? (
                  paginatedChauffeurs.map((chauffeur) => (
                    <TableRow key={chauffeur._id} hover>
                      <TableCell>{chauffeur.driverId}</TableCell>
                      <TableCell>{`${chauffeur.lastName} ${chauffeur.firstName}`}</TableCell>
                      <TableCell>{chauffeur.phone1 || '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(chauffeur.licenseTypes || chauffeur.licenseType).split(', ').map((type, idx) => (
                            <Chip
                              key={idx}
                              label={type.trim()}
                              size="small"
                              color={['C', 'CE', 'C1', 'C1E'].includes(type.trim()) ? 'warning' : 
                                     ['D', 'DE', 'D1', 'D1E'].includes(type.trim()) ? 'error' :
                                     ['E', 'BE'].includes(type.trim()) ? 'secondary' : 'primary'}
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{chauffeur.licenseNumber}</TableCell>
                      <TableCell>{chauffeur.licenseIssuedBy || '-'}</TableCell>
                      <TableCell>{chauffeur.licenseIssueDate ? new Date(chauffeur.licenseIssueDate).toLocaleDateString('fr-FR') : '-'}</TableCell>
                      <TableCell>{chauffeur.licenseExpiryDate ? new Date(chauffeur.licenseExpiryDate).toLocaleDateString('fr-FR') : '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={chauffeur.status}
                          color={
                            chauffeur.status === "disponible"
                              ? "success"
                              : chauffeur.status === "indisponible"
                              ? "warning"
                              : "error"
                          }
                          size="small"
                          onClick={(e) => handleQuickToggleStatus(chauffeur, e)}
                          sx={{
                            fontWeight: "medium",
                            textTransform: "capitalize",
                            cursor: "pointer",
                            "&:hover": { opacity: 0.8, transform: "scale(1.05)" },
                            transition: "all 0.2s ease",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, chauffeur._id!)}
                          sx={{ p: { xs: 0.5, sm: 1 } }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <Typography variant="body1" color="textSecondary">
                        {searchTerm
                          ? "Aucun résultat pour votre recherche."
                          : "Aucun chauffeur trouvé."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {paginatedChauffeurs.length > 0 ? (
              paginatedChauffeurs.map((chauffeur) => (
                <Card key={chauffeur._id} sx={{ borderRadius: 3, boxShadow: 2 }}>
                  <CardContent sx={{ p: 2, pb: "16px !important" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {chauffeur.firstName} {chauffeur.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                          <span style={{ backgroundColor: "#e0e0e0", padding: "2px 6px", borderRadius: 4, fontSize: "0.75rem", fontWeight: "bold" }}>
                            {chauffeur.driverId}
                          </span>
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Chip
                          label={chauffeur.status}
                          color={
                            chauffeur.status === "disponible"
                              ? "success"
                              : chauffeur.status === "indisponible"
                              ? "warning"
                              : "error"
                          }
                          size="small"
                          onClick={(e) => handleQuickToggleStatus(chauffeur, e)}
                          sx={{ 
                            mr: 1, 
                            fontSize: "0.7rem", 
                            height: 24,
                            cursor: "pointer",
                            "&:hover": { opacity: 0.8, transform: "scale(1.05)" },
                            transition: "all 0.2s ease",
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, chauffeur._id!)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 1.5 }} />
                    
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2" component="a" href={`tel:${chauffeur.phone1}`} sx={{ textDecoration: "none", color: "primary.main", fontWeight: 500 }}>
                          {chauffeur.phone1}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <LicenseIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          Permis {chauffeur.licenseTypes || chauffeur.licenseType}
                        </Typography>
                      </Box>
                      
                      {chauffeur.licenseExpiryDate && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, gridColumn: "span 2" }}>
                          <TimeIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            Expire le: {new Date(chauffeur.licenseExpiryDate).toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <Typography variant="body1" color="textSecondary">
                  {searchTerm
                    ? "Aucun résultat."
                    : "Aucun chauffeur."}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredChauffeurs.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Chauffeurs par page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} sur ${count}`
          }
          sx={{ mt: 2, borderTop: "1px solid", borderColor: "divider" }}
        />

        {/* Menu d'actions */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: { boxShadow: 3, borderRadius: 1.5, minWidth: 180 },
          }}
        >
          <MenuItem
            onClick={() => {
              handleOpenModal(
                chauffeurs.find((c) => c._id === selectedChauffeurId)
              );
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Modifier</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              const driver = chauffeurs.find((c) => c._id === selectedChauffeurId);
              if (driver) {
                setDetailsChauffeur(driver);
                setOpenDetails(true);
              }
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Voir Détails</ListItemText>
          </MenuItem>
          <MenuItem
            component={Link}
            to={`/chauffeurs/historique/${selectedChauffeurId}`}
            onClick={() => {
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <HistoryIconIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Historique</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              const driver = chauffeurs.find((c) => c._id === selectedChauffeurId);
              if (driver) setQrDriver(driver);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <QrCodeIcon fontSize="small" sx={{ color: "purple" }} />
            </ListItemIcon>
            <ListItemText>QR Code</ListItemText>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={() => {
              handleDelete(selectedChauffeurId!);
              handleMenuClose();
            }}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
            </ListItemIcon>
            <ListItemText>Supprimer</ListItemText>
          </MenuItem>
        </Menu>

        {/* Modal de création/modification */}
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
        >
          <DialogTitle
            sx={{
              fontWeight: "bold",
              borderBottom: "1px solid",
              borderColor: "divider",
              pb: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            {editingChauffeur
              ? "Modifier le chauffeur"
              : "Créer un nouveau chauffeur"}
            {isMobile && (
              <IconButton onClick={handleCloseModal} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent sx={{ pt: "20px !important" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Section Informations Personnelles */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, fontWeight: "bold" }}
                  >
                    Informations Personnelles
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="driverId"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="ID Chauffeur (unique)"
                            fullWidth
                            required
                            variant="outlined"
                            size="small"
                            error={!!errors.driverId}
                            helperText={errors.driverId?.message || (suggestedDriverId && !editingChauffeur ? `✨ Suggéré : ${suggestedDriverId}` : '')}
                            InputProps={{
                              endAdornment: !editingChauffeur && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const newId = generateNextDriverId();
                                    setValue('driverId', newId);
                                    setSuggestedDriverId(newId);
                                    enqueueSnackbar('✅ ID généré automatiquement', { variant: 'info' });
                                  }}
                                  title="Générer un nouvel ID"
                                >
                                  <span style={{ fontSize: '16px' }}>🔄</span>
                                </IconButton>
                              )
                            }}
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            label="Statut"
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.status}
                            helperText={errors.status?.message}
                          >
                            <MenuItem value="disponible">Disponible</MenuItem>
                            <MenuItem value="indisponible">
                              Indisponible
                            </MenuItem>
                            <MenuItem value="inactif">Inactif</MenuItem>
                          </TextField>
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="lastName"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Nom de famille"
                            fullWidth
                            required
                            variant="outlined"
                            size="small"
                            error={!!errors.lastName}
                            helperText={errors.lastName?.message}
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="firstName"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Prénom"
                            fullWidth
                            required
                            variant="outlined"
                            size="small"
                            error={!!errors.firstName}
                            helperText={errors.firstName?.message}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Section Coordonnées */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, fontWeight: "bold" }}
                  >
                    Coordonnées
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="phone1"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Téléphone principal"
                            fullWidth
                            required
                            variant="outlined"
                            size="small"
                            error={!!errors.phone1}
                            helperText={errors.phone1?.message}
                            onChange={(e) => {
                              const formatted = formatPhone(e.target.value);
                              field.onChange(formatted);
                            }}
                            InputProps={{
                              endAdornment: watchedPhone1 && watchedPhone1.replace(/\D/g, '').length === 10 ? (
                                <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                              ) : watchedPhone1 && watchedPhone1.length > 0 ? (
                                <Error sx={{ color: 'error.main', fontSize: 20 }} />
                              ) : null
                            }}
                            placeholder="06 12 34 56 78"
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="phone2"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Téléphone secondaire"
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.phone2}
                            helperText={errors.phone2?.message}
                            onChange={(e) => {
                              const formatted = formatPhone(e.target.value);
                              field.onChange(formatted);
                            }}
                            InputProps={{
                              endAdornment: watchedPhone2 && watchedPhone2.replace(/\D/g, '').length === 10 ? (
                                <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                              ) : watchedPhone2 && watchedPhone2.length > 0 && watchedPhone2.replace(/\D/g, '').length > 0 ? (
                                <Error sx={{ color: 'error.main', fontSize: 20 }} />
                              ) : null
                            }}
                            placeholder="06 12 34 56 78"
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Email"
                            type="email"
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            InputProps={{
                              endAdornment: watchedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(watchedEmail) ? (
                                <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                              ) : watchedEmail && watchedEmail.length > 0 ? (
                                <Error sx={{ color: 'error.main', fontSize: 20 }} />
                              ) : null
                            }}
                            placeholder="jean.dupont@email.com"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Section Adresse */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, fontWeight: "bold" }}
                  >
                    Adresse
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="address"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Adresse"
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.address}
                            helperText={errors.address?.message}
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="postalCode"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Code postal"
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.postalCode}
                            helperText={errors.postalCode?.message}
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="city"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Ville"
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.city}
                            helperText={errors.city?.message}
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="department"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Département"
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.department}
                            helperText={errors.department?.message}
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="country"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Pays"
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.country}
                            helperText={errors.country?.message}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Section Permis de Conduire */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, fontWeight: "bold" }}
                  >
                    Permis de Conduire
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="licenseType"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            label="Type de permis"
                            fullWidth
                            required
                            variant="outlined"
                            size="small"
                            error={!!errors.licenseType}
                            helperText={errors.licenseType?.message}
                          >
                            {licenseTypes.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="licenseNumber"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Numéro de permis (unique)"
                            fullWidth
                            required
                            variant="outlined"
                            size="small"
                            error={!!errors.licenseNumber}
                            helperText={errors.licenseNumber?.message}
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="licenseExpiryDate"
                        control={control}
                        render={({ field }) => {
                          // ✨ Vérifier si le permis expire bientôt
                          let expiryWarning = '';
                          let expiryColor = 'default';
                          
                          if (field.value) {
                            const expiryDate = new Date(field.value);
                            const today = new Date();
                            const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                            
                            if (daysUntilExpiry < 0) {
                              expiryWarning = `⚠️ Permis expiré depuis ${Math.abs(daysUntilExpiry)} jours`;
                              expiryColor = 'error';
                            } else if (daysUntilExpiry < 30) {
                              expiryWarning = `⚠️ Expire dans ${daysUntilExpiry} jours`;
                              expiryColor = 'warning';
                            } else if (daysUntilExpiry < 90) {
                              expiryWarning = `ℹ️ Expire dans ${daysUntilExpiry} jours`;
                              expiryColor = 'info';
                            }
                          }
                          
                          return (
                            <TextField
                              {...field}
                              label="Date d'expiration"
                              type="date"
                              fullWidth
                              required
                              variant="outlined"
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              error={!!errors.licenseExpiryDate || expiryColor === 'error'}
                              helperText={expiryWarning || errors.licenseExpiryDate?.message || "✨ Auto-calculée si date d'émission saisie"}
                              FormHelperTextProps={{
                                sx: { 
                                  color: expiryColor === 'error' ? 'error.main' : expiryColor === 'warning' ? 'warning.main' : expiryColor === 'info' ? 'info.main' : 'text.secondary'
                                }
                              }}
                            />
                          );
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="licenseIssueDate"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Date de délivrance"
                            type="date"
                            fullWidth
                            variant="outlined"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.licenseIssueDate}
                            helperText={errors.licenseIssueDate?.message || "✨ Calculera automatiquement la date d'expiration"}
                            onChange={(e) => {
                              field.onChange(e);
                              // ✨ Calcul automatique de la date d'expiration
                              if (e.target.value && watchedLicenseType) {
                                const calculatedExpiry = calculateExpiryDate(e.target.value, watchedLicenseType);
                                if (calculatedExpiry) {
                                  setValue('licenseExpiryDate', calculatedExpiry);
                                  enqueueSnackbar(`✨ Date d'expiration calculée : ${new Date(calculatedExpiry).toLocaleDateString('fr-FR')}`, { variant: 'info', autoHideDuration: 2000 });
                                }
                              }
                            }}
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                      <Controller
                        name="licenseIssuedBy"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Délivré par"
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={!!errors.licenseIssuedBy}
                            helperText={errors.licenseIssuedBy?.message}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Section Informations Poids Lourd */}
                {(watchedLicenseType === "C" ||
                  watchedLicenseType === "C1" ||
                  watchedLicenseType === "D" ||
                  watchedLicenseType === "D1" ||
                  watchedLicenseType === "CE" ||
                  watchedLicenseType === "C1E" ||
                  watchedLicenseType === "DE" ||
                  watchedLicenseType === "D1E" ||
                  watchedLicenseType === "BE") && (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: "bold" }}
                    >
                      Informations Poids Lourd
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
                      <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                        <Controller
                          name="heavyVehicleInfo.driverCardExpiryDate"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Fin validité Carte Conducteur"
                              type="date"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                        <Controller
                          name="heavyVehicleInfo.fcoExpiryDate"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Fin validité FCO"
                              type="date"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                        <Controller
                          name="heavyVehicleInfo.kacesExpiryDate"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Fin validité KACES"
                              type="date"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                        <Controller
                          name="heavyVehicleInfo.adrExpiryDate"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Fin validité ADR"
                              type="date"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        />
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                pt: "16px !important",
                px: 3,
                pb: 2,
              }}
            >
              <Button onClick={handleCloseModal} color="inherit">
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={createOrUpdateMutation.isPending}
                startIcon={
                  createOrUpdateMutation.isPending ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
              >
                {editingChauffeur
                  ? "Enregistrer les modifications"
                  : "Créer le chauffeur"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Dialog Détails Chauffeur */}
        <Dialog
          open={openDetails}
          onClose={() => setOpenDetails(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 3,
              overflow: "hidden",
              background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
            },
          }}
        >
          {detailsChauffeur && (
            <>
              {/* Header avec gradient */}
              <Box
                sx={{
                  background:
                    detailsChauffeur.status === "inactif"
                      ? "linear-gradient(135deg, #78716c 0%, #57534e 100%)"
                      : detailsChauffeur.status === "indisponible"
                      ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                      : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  color: "white",
                  p: 3,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      sx={{ mb: 1, fontSize: { xs: "1.5rem", sm: "2rem" } }}
                    >
                      {detailsChauffeur.firstName} {detailsChauffeur.lastName}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      ID: {detailsChauffeur.driverId}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => setOpenDetails(false)}
                    sx={{ color: "white" }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              <DialogContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {/* Informations Personnelles */}
                  <Card elevation={2}>
                    <CardContent>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PhoneIcon color="primary" />
                        Informations Personnelles
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Statut
                          </Typography>
                          <Chip
                            label={
                              detailsChauffeur.status === "disponible"
                                ? "Disponible"
                                : detailsChauffeur.status === "indisponible"
                                ? "Indisponible"
                                : "Inactif"
                            }
                            color={
                              detailsChauffeur.status === "disponible"
                                ? "success"
                                : detailsChauffeur.status === "indisponible"
                                ? "warning"
                                : "default"
                            }
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Téléphone principal
                          </Typography>
                          <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                            {detailsChauffeur.phone1 || "—"}
                          </Typography>
                        </Box>
                        {detailsChauffeur.phone2 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Téléphone secondaire
                            </Typography>
                            <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                              {detailsChauffeur.phone2}
                            </Typography>
                          </Box>
                        )}
                        {detailsChauffeur.email && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Email
                            </Typography>
                            <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                              {detailsChauffeur.email}
                            </Typography>
                          </Box>
                        )}
                        {detailsChauffeur.address && (
                          <Box sx={{ gridColumn: { xs: "1", sm: "1 / -1" } }}>
                            <Typography variant="caption" color="text.secondary">
                              Adresse
                            </Typography>
                            <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                              {detailsChauffeur.address}
                              {detailsChauffeur.postalCode && `, ${detailsChauffeur.postalCode}`}
                              {detailsChauffeur.city && ` ${detailsChauffeur.city}`}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Informations Permis */}
                  <Card elevation={2}>
                    <CardContent>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LicenseIcon color="primary" />
                        Informations Permis
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Types de permis
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {(detailsChauffeur.licenseTypes || detailsChauffeur.licenseType).split(', ').map((type, idx) => (
                              <Chip
                                key={idx}
                                label={type.trim()}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />
                            ))}
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Numéro de permis
                          </Typography>
                          <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                            {detailsChauffeur.licenseNumber || "—"}
                          </Typography>
                        </Box>
                        {detailsChauffeur.licenseIssueDate && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Date d'obtention
                            </Typography>
                            <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                              {new Date(detailsChauffeur.licenseIssueDate).toLocaleDateString("fr-FR")}
                            </Typography>
                          </Box>
                        )}
                        {detailsChauffeur.licenseExpiryDate && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Date d'expiration
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight={500}
                              sx={{
                                mt: 0.5,
                                color:
                                  new Date(detailsChauffeur.licenseExpiryDate) < new Date()
                                    ? "error.main"
                                    : new Date(detailsChauffeur.licenseExpiryDate) <
                                      new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)
                                    ? "warning.main"
                                    : "text.primary",
                              }}
                            >
                              {new Date(detailsChauffeur.licenseExpiryDate).toLocaleDateString("fr-FR")}
                              {new Date(detailsChauffeur.licenseExpiryDate) < new Date() && (
                                <Chip
                                  label="Expiré"
                                  color="error"
                                  size="small"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Typography>
                          </Box>
                        )}
                        {detailsChauffeur.licenseIssuedBy && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Délivré par
                            </Typography>
                            <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                              {detailsChauffeur.licenseIssuedBy}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Informations Poids Lourd */}
                  {detailsChauffeur.heavyVehicleInfo &&
                    (detailsChauffeur.heavyVehicleInfo.driverCardExpiryDate ||
                      detailsChauffeur.heavyVehicleInfo.fcoExpiryDate ||
                      detailsChauffeur.heavyVehicleInfo.kacesExpiryDate ||
                      detailsChauffeur.heavyVehicleInfo.adrExpiryDate) && (
                      <Card elevation={2}>
                        <CardContent>
                          <Typography
                            variant="h6"
                            fontWeight={600}
                            sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
                          >
                            <Box component="img" src="/vehicule-icon.png" sx={{ width: 24, height: 24 }} />
                            Informations Poids Lourd
                          </Typography>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                              gap: 2,
                            }}
                          >
                            {detailsChauffeur.heavyVehicleInfo.driverCardExpiryDate && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Fin validité Carte Conducteur
                                </Typography>
                                <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                                  {new Date(
                                    detailsChauffeur.heavyVehicleInfo.driverCardExpiryDate
                                  ).toLocaleDateString("fr-FR")}
                                </Typography>
                              </Box>
                            )}
                            {detailsChauffeur.heavyVehicleInfo.fcoExpiryDate && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Fin validité FCO
                                </Typography>
                                <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                                  {new Date(
                                    detailsChauffeur.heavyVehicleInfo.fcoExpiryDate
                                  ).toLocaleDateString("fr-FR")}
                                </Typography>
                              </Box>
                            )}
                            {detailsChauffeur.heavyVehicleInfo.kacesExpiryDate && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Fin validité KACES
                                </Typography>
                                <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                                  {new Date(
                                    detailsChauffeur.heavyVehicleInfo.kacesExpiryDate
                                  ).toLocaleDateString("fr-FR")}
                                </Typography>
                              </Box>
                            )}
                            {detailsChauffeur.heavyVehicleInfo.adrExpiryDate && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Fin validité ADR
                                </Typography>
                                <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                                  {new Date(
                                    detailsChauffeur.heavyVehicleInfo.adrExpiryDate
                                  ).toLocaleDateString("fr-FR")}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    )}

                  {/* Historique des modifications */}
                  {detailsChauffeur.dataModifications && detailsChauffeur.dataModifications.length > 0 && (
                    <Card elevation={2}>
                      <CardContent>
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <HistoryIconIcon color="primary" />
                          Historique des modifications ({detailsChauffeur.dataModifications.length})
                        </Typography>
                        <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                          {detailsChauffeur.dataModifications.slice().reverse().map((mod, idx) => (
                            <Box key={idx} sx={{ mb: 2, pb: 2, borderBottom: idx < detailsChauffeur.dataModifications!.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(mod.date).toLocaleDateString('fr-FR')} - {mod.source === 'import' ? 'Import' : mod.source === 'manual' ? 'Manuel' : 'API'}
                              </Typography>
                              {mod.changes.map((change, cidx) => (
                                <Typography key={cidx} variant="body2" sx={{ ml: 1 }}>
                                  <strong>{FIELD_LABELS[change.field] || change.field}:</strong>{' '}
                                  <span style={{ color: '#ef4444', textDecoration: 'line-through' }}>{change.oldValue || '—'}</span>
                                  {' → '}
                                  <span style={{ color: '#22c55e' }}>{change.newValue || '—'}</span>
                                </Typography>
                              ))}
                              {mod.comment && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', ml: 1 }}>
                                  Note: {mod.comment}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </DialogContent>

              <DialogActions sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
                <Button
                  component={Link}
                  to={`/chauffeurs/historique/${detailsChauffeur._id || detailsChauffeur.driverId}`}
                  variant="outlined"
                  startIcon={<HistoryIconIcon />}
                >
                  Voir l'historique
                </Button>
                <Button
                  onClick={() => {
                    setOpenDetails(false);
                    setEditingChauffeur(detailsChauffeur);
                    setOpenModal(true);
                  }}
                  variant="contained"
                  startIcon={<EditIcon />}
                >
                  Modifier
                </Button>
                <Button onClick={() => setOpenDetails(false)} variant="outlined">
                  Fermer
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* QR Code Dialog */}
        {qrDriver && (
          <DriverQRCode
            driverId={qrDriver.driverId}
            firstName={qrDriver.firstName}
            lastName={qrDriver.lastName}
            licenseType={qrDriver.licenseType}
            open={!!qrDriver}
            onClose={() => setQrDriver(null)}
          />
        )}
      </Box>
    </Box>
  );
};

export default ChauffeursPage;
